import { addDays, addMonths, daysInMonth, weekday, type LocalDate } from '../dates';
import type { DateRange } from '../ledger/counting';

export type WeekStart = 0 | 1; // 0 = Sunday, 1 = Monday
export type RangePreset = 'week' | 'month' | 'quarter' | 'year';
export type TimeGrain = 'day' | 'week' | 'month';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n: number) => String(n).padStart(2, '0');

export function weekStartOf(d: LocalDate, weekStartsOn: WeekStart = 1): LocalDate {
  return addDays(d, -((weekday(d) - weekStartsOn + 7) % 7));
}

export function monthStart(d: LocalDate): LocalDate {
  return `${d.slice(0, 7)}-01`;
}

export function monthEnd(d: LocalDate): LocalDate {
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  return `${d.slice(0, 7)}-${pad(daysInMonth(y, m))}`;
}

export function presetRange(
  preset: RangePreset,
  anchor: LocalDate,
  weekStartsOn: WeekStart = 1,
): DateRange {
  switch (preset) {
    case 'week': {
      const start = weekStartOf(anchor, weekStartsOn);
      return { start, end: addDays(start, 6) };
    }
    case 'month':
      return { start: monthStart(anchor), end: monthEnd(anchor) };
    case 'quarter': {
      const m = Number(anchor.slice(5, 7));
      const firstMonth = Math.floor((m - 1) / 3) * 3 + 1;
      const start = `${anchor.slice(0, 4)}-${pad(firstMonth)}-01`;
      return { start, end: monthEnd(addMonths(start, 2)) };
    }
    case 'year': {
      const y = anchor.slice(0, 4);
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
  }
}

const MONTHS_PER: Record<Exclude<RangePreset, 'week'>, number> = { month: 1, quarter: 3, year: 12 };

/** The preset period `steps` periods away (negative = earlier). */
export function shiftPreset(
  preset: RangePreset,
  range: DateRange,
  steps: number,
  weekStartsOn: WeekStart = 1,
): DateRange {
  const anchor =
    preset === 'week'
      ? addDays(range.start, 7 * steps)
      : addMonths(range.start, steps * MONTHS_PER[preset]);
  return presetRange(preset, anchor, weekStartsOn);
}

function utcMs(d: LocalDate): number {
  return Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
}

/** Calendar days from a to b (b − a). Pure calendar math, so time zones and DST can't shift it. */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  return Math.round((utcMs(b) - utcMs(a)) / 86_400_000);
}

export function rangeLength(r: DateRange): number {
  return daysBetween(r.start, r.end) + 1;
}

export function eachDay(r: DateRange): LocalDate[] {
  const out: LocalDate[] = [];
  for (let d = r.start; d <= r.end; d = addDays(d, 1)) out.push(d);
  return out;
}

/** A range of the same length that ends the day before `r` starts (for custom ranges). */
export function previousRange(r: DateRange): DateRange {
  const end = addDays(r.start, -1);
  return { start: addDays(end, -(rangeLength(r) - 1)), end };
}

export function isValidRange(r: DateRange): boolean {
  return DATE_RE.test(r.start) && DATE_RE.test(r.end) && r.start <= r.end;
}

/** Column size for an over-time chart: at most 14 columns of days, weeks up to ~2 months, then months. */
export function timeGrain(r: DateRange): TimeGrain {
  const n = rangeLength(r);
  if (n <= 14) return 'day';
  if (n <= 62) return 'week';
  return 'month';
}
